// customer_list_v1: Customer master CRUD page (inline form pattern, follows Products.js)
import React, { useState, useEffect } from 'react';
import API from '../api';
import { Users, Plus, Edit2, Trash2, Save, X, Power, Search, RefreshCw, Send } from 'lucide-react';
import toast from 'react-hot-toast';

const emptyForm = () => ({
  name: '', phone: '', email: '', address: '',
  telegram_chat_id: '', line_user_id: '', note: '',
});

function formatMoney(n) {
  return Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ฿';
}

export default function Customers() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all|has_tg|no_tg
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const loadStats = async () => {
    try {
      const { data } = await API.get('/api/customers/stats/summary');
      setStats(data);
    } catch (e) { console.error('Stats error:', e); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter === 'has_tg') params.append('has_tg', 'true');
      if (filter === 'no_tg') params.append('has_tg', 'false');
      if (search.trim()) params.append('search', search.trim());
      params.append('limit', '500');
      const { data } = await API.get('/api/customers?' + params.toString());
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Load customers error:', e);
      toast.error('โหลดลูกค้าไม่สำเร็จ');
      setList([]);
    }
    setLoading(false);
  };

  useEffect(() => { loadStats(); }, []);

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, search]);

  const refreshAll = () => { loadStats(); load(); };

  const handleEdit = (c) => {
    setEditId(c.id);
    setForm({
      name: c.name || '',
      phone: c.phone || '',
      email: c.email || '',
      address: c.address || '',
      telegram_chat_id: c.telegram_chat_id || '',
      line_user_id: c.line_user_id || '',
      note: c.note || '',
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setForm(emptyForm());
    setEditId(null);
    setShowForm(false);
  };

  const validate = () => {
    if (!form.name.trim()) return 'กรุณากรอกชื่อลูกค้า';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        telegram_chat_id: form.telegram_chat_id.trim() || null,
        line_user_id: form.line_user_id.trim() || null,
        note: form.note.trim() || null,
      };
      if (editId) {
        await API.put('/api/customers/' + editId, payload);
        toast.success('อัปเดตลูกค้าสำเร็จ');
      } else {
        await API.post('/api/customers', payload);
        toast.success('เพิ่มลูกค้าสำเร็จ');
      }
      handleCancelEdit();
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'บันทึกไม่สำเร็จ');
    }
    setSaving(false);
  };

  const handleToggle = async (c) => {
    try {
      await API.patch('/api/customers/' + c.id + '/toggle');
      toast.success(c.is_active ? 'ปิดลูกค้าแล้ว' : 'เปิดลูกค้าแล้ว');
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'เปลี่ยนสถานะไม่สำเร็จ');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm('ลบลูกค้า "' + c.name + '" ?\nบิลเก่าจะถูก unlink อัตโนมัติ (ไม่หาย)')) return;
    try {
      await API.delete('/api/customers/' + c.id);
      toast.success('ลบลูกค้าสำเร็จ');
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'ลบไม่สำเร็จ');
    }
  };

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 28px', background:'#f8fafc', minHeight:'100%' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent:'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 16 : 24,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap: 10 }}>
          <Users size={isMobile ? 22 : 26} color="#0ea5e9" />
          <div>
            <h1 style={{ margin:0, fontSize: isMobile ? 18 : 22, fontWeight:700, color:'#1e293b' }}>จัดการลูกค้า</h1>
            <p style={{ margin:0, fontSize: isMobile ? 12 : 13, color:'#64748b' }}>
              รายชื่อลูกค้าสำหรับใช้ในบิล + Telegram chat_id สำหรับส่งบิลอัตโนมัติ
            </p>
          </div>
        </div>
        {!showForm && (
          <button onClick={() => { setEditId(null); setForm(emptyForm()); setShowForm(true); }}
            style={{
              display:'flex', alignItems:'center', justifyContent:'center', gap:6,
              background:'#0ea5e9', color:'#fff', border:'none', borderRadius:8,
              padding: isMobile ? '12px 18px' : '10px 18px',
              fontSize: isMobile ? 14 : 13, fontWeight:600, cursor:'pointer',
            }}>
            <Plus size={isMobile ? 16 : 15} />เพิ่มลูกค้า
          </button>
        )}
      </div>

      {/* Inline Form */}
      {showForm && (
        <div style={{
          background:'#fff', borderRadius: 14,
          padding: isMobile ? 16 : 22,
          boxShadow:'0 1px 6px rgba(0,0,0,0.07)',
          marginBottom: isMobile ? 16 : 20,
        }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 14 }}>
            <h2 style={{ margin:0, fontSize: isMobile ? 14 : 16, fontWeight: 700, color:'#1e293b' }}>
              {editId ? '✏️ แก้ไขลูกค้า' : '➕ เพิ่มลูกค้าใหม่'}
            </h2>
            <button onClick={handleCancelEdit}
              style={{ background:'none', border:'none', cursor:'pointer', padding: 4 }}>
              <X size={20} color="#94a3b8" />
            </button>
          </div>

          {/* Row 1: name | phone | email */}
          <div style={{
            display:'grid',
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1.5fr',
            gap: 12, marginBottom: 12,
          }}>
            <div>
              <Label>ชื่อ-นามสกุล *</Label>
              <input value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="เช่น คุณสมชาย ใจดี"
                style={inputStyle(isMobile)} />
            </div>
            <div>
              <Label>เบอร์โทร</Label>
              <input value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="081-234-5678"
                style={inputStyle(isMobile)} />
            </div>
            <div>
              <Label>อีเมล</Label>
              <input value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="email@example.com"
                style={inputStyle(isMobile)} />
            </div>
          </div>

          {/* Telegram highlight box */}
          <div style={{
            background:'#e0f2fe', borderRadius: 10, padding: 12, marginBottom: 12,
            border: '1px solid #bae6fd',
          }}>
            <Label color="#0284c7">
              <Send size={12} style={{ display:'inline', verticalAlign:'middle', marginRight: 4 }} />
              Telegram chat_id (สำหรับส่งบิลอัตโนมัติ)
            </Label>
            <input value={form.telegram_chat_id}
              onChange={e => setForm({ ...form, telegram_chat_id: e.target.value })}
              placeholder="เช่น 123456789 (private) หรือ -1003757595195 (group)"
              style={{
                ...inputStyle(isMobile),
                fontFamily: 'monospace',
                background: '#fff',
                border: '1px solid #bae6fd',
              }} />
            <div style={{ fontSize: 10, color:'#0c4a7c', marginTop: 6 }}>
              💡 หา chat_id ได้จากการให้ลูกค้าทักบอท แล้วระบบจะแสดงให้ใน Inbox
            </div>
          </div>

          {/* Address */}
          <div style={{ marginBottom: 12 }}>
            <Label>ที่อยู่</Label>
            <input value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })}
              placeholder="ที่อยู่จัดส่งใบกำกับ (ถ้ามี)"
              style={inputStyle(isMobile)} />
          </div>

          {/* Note */}
          <div style={{ marginBottom: 14 }}>
            <Label>หมายเหตุ</Label>
            <textarea value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
              placeholder="หมายเหตุเพิ่มเติม..."
              rows={2}
              style={{ ...inputStyle(isMobile), resize:'vertical', fontFamily:'inherit' }} />
          </div>

          {/* Action buttons */}
          <div style={{ display:'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button onClick={handleCancelEdit} disabled={saving}
              style={{
                padding: isMobile ? '12px 18px' : '10px 18px',
                background:'#f1f5f9', color:'#475569', border:'none', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor:'pointer',
              }}>ยกเลิก</button>
            <button onClick={handleSave} disabled={saving}
              style={{
                padding: isMobile ? '12px 22px' : '10px 22px',
                background: saving ? '#94a3b8' : '#0ea5e9', color:'#fff',
                border:'none', borderRadius: 8, fontSize: 14, fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                display:'inline-flex', alignItems:'center', gap: 6,
              }}>
              <Save size={15} />
              {saving ? 'กำลังบันทึก...' : (editId ? 'บันทึก' : 'เพิ่มลูกค้า')}
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards (4 cols desktop / 2x2 mobile) */}
      <div style={{
        display:'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: isMobile ? 10 : 14,
        marginBottom: isMobile ? 16 : 20,
      }}>
        <StatCard label="ทั้งหมด"      value={stats?.total || 0}         color="#64748b" icon="👥" />
        <StatCard label="มี Telegram"   value={stats?.with_telegram || 0} color="#0ea5e9" icon="✈️" />
        <StatCard label="มีบิลแล้ว"     value={stats?.with_invoices || 0} color="#16a34a" icon="🧾" />
        <StatCard label="ยอดรวม"        value={formatMoney(stats?.total_amount || 0)} color="#a16207" icon="💰" isText />
      </div>

      {/* Filter bar */}
      <div style={{
        background:'#fff', padding: isMobile ? 12 : 14, borderRadius: 12,
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
        marginBottom: isMobile ? 12 : 16,
        display:'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 10, alignItems: 'stretch',
      }}>
        <div style={{ position:'relative', flex: 1 }}>
          <Search size={16} color="#94a3b8" style={{ position:'absolute', left: 12, top:'50%', transform:'translateY(-50%)' }} />
          <input
            type="text" placeholder="ค้นหา ชื่อ / เบอร์ / Telegram ID / อีเมล..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', boxSizing:'border-box',
              padding: isMobile ? '12px 14px 12px 36px' : '9px 12px 9px 36px',
              borderRadius: 8, border: '1px solid #e2e8f0',
              fontSize: isMobile ? 16 : 14, outline:'none',
            }} />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          style={{
            padding: isMobile ? '12px 14px' : '9px 12px',
            borderRadius: 8, border: '1px solid #e2e8f0',
            fontSize: isMobile ? 16 : 14, outline:'none',
            background:'#fff', cursor:'pointer',
            minWidth: isMobile ? '100%' : 160,
          }}>
          <option value="all">ทั้งหมด</option>
          <option value="has_tg">มี Telegram</option>
          <option value="no_tg">ไม่มี Telegram</option>
        </select>
        <button onClick={refreshAll}
          style={{
            display:'flex', alignItems:'center', justifyContent:'center', gap:6,
            padding: isMobile ? '12px 18px' : '9px 16px',
            background:'#f1f5f9', color:'#64748b', border:'none', borderRadius: 8,
            cursor:'pointer', fontSize: isMobile ? 14 : 13, fontWeight:500,
          }}>
          <RefreshCw size={14} />รีเฟรช
        </button>
      </div>

      {/* List */}
      <div style={{ background:'#fff', borderRadius: 12, boxShadow:'0 1px 6px rgba(0,0,0,0.05)', overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign:'center', color:'#94a3b8' }}>กำลังโหลด...</div>
        ) : list.length === 0 ? (
          <div style={{ padding: 60, textAlign:'center', color:'#94a3b8' }}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>👥</div>
            <div style={{ fontSize: 16, fontWeight: 600, color:'#64748b', marginBottom: 6 }}>
              {search || filter !== 'all' ? 'ไม่พบลูกค้าที่ตรงกับเงื่อนไข' : 'ยังไม่มีลูกค้า'}
            </div>
            <div style={{ fontSize: 13, color:'#94a3b8' }}>
              {search || filter !== 'all' ? 'ลองล้างตัวกรองดู' : 'คลิก "เพิ่มลูกค้า" เพื่อเริ่มต้น'}
            </div>
          </div>
        ) : isMobile ? (
          <div>
            {list.map((c, i) => (
              <CustomerCard key={c.id} c={c} isLast={i === list.length - 1}
                onEdit={handleEdit} onToggle={handleToggle} onDelete={handleDelete} />
            ))}
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background:'#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <Th>ลูกค้า</Th>
                  <Th>เบอร์โทร</Th>
                  <Th>Telegram</Th>
                  <Th align="center">บิล</Th>
                  <Th align="right">ยอดรวม</Th>
                  <Th align="center">จัดการ</Th>
                </tr>
              </thead>
              <tbody>
                {list.map(c => (
                  <tr key={c.id} style={{
                    borderBottom: '1px solid #f1f5f9',
                    opacity: c.is_active ? 1 : 0.55,
                  }}>
                    <Td>
                      <div style={{ fontWeight: 600, color:'#1e293b' }}>{c.name}</div>
                      {c.email && (
                        <div style={{ fontSize: 11, color:'#94a3b8', marginTop: 2 }}>{c.email}</div>
                      )}
                    </Td>
                    <Td>{c.phone || <span style={{ color:'#cbd5e1' }}>—</span>}</Td>
                    <Td>
                      {c.telegram_chat_id ? (
                        <span style={{
                          background:'#e0f2fe', color:'#0c4a7c', fontFamily:'monospace',
                          padding: '3px 8px', borderRadius: 6, fontSize: 11,
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}>
                          <Send size={10} />{c.telegram_chat_id}
                        </span>
                      ) : (
                        <span style={{ color:'#cbd5e1', fontSize: 11 }}>— ไม่มี</span>
                      )}
                    </Td>
                    <Td align="center">
                      <span style={{
                        background: (c.invoice_count || 0) > 0 ? '#dcfce7' : '#f8fafc',
                        color: (c.invoice_count || 0) > 0 ? '#166534' : '#94a3b8',
                        fontWeight: 600, padding: '3px 9px', borderRadius: 6, fontSize: 12,
                      }}>{c.invoice_count || 0}</span>
                    </Td>
                    <Td align="right">
                      <div style={{ fontWeight: 600, color:'#0284c7' }}>
                        {formatMoney(c.total_amount)}
                      </div>
                    </Td>
                    <Td align="center">
                      <div style={{ display:'inline-flex', gap: 4 }}>
                        <IconBtn title="แก้ไข" onClick={() => handleEdit(c)} color="#64748b">
                          <Edit2 size={14} />
                        </IconBtn>
                        <IconBtn title={c.is_active ? 'ปิด' : 'เปิด'} onClick={() => handleToggle(c)} color={c.is_active ? '#ef4444' : '#16a34a'}>
                          <Power size={14} />
                        </IconBtn>
                        <IconBtn title="ลบ" onClick={() => handleDelete(c)} color="#ef4444">
                          <Trash2 size={14} />
                        </IconBtn>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && list.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color:'#94a3b8', textAlign:'center' }}>
          แสดง {list.length} รายการ
        </div>
      )}
    </div>
  );
}

// ---------- subcomponents ----------
function CustomerCard({ c, isLast, onEdit, onToggle, onDelete }) {
  return (
    <div style={{
      padding: 14,
      borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
      opacity: c.is_active ? 1 : 0.55,
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: 8, gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color:'#1e293b' }}>{c.name}</div>
          {c.phone && <div style={{ fontSize: 12, color:'#64748b', marginTop: 2 }}>📞 {c.phone}</div>}
          {c.email && <div style={{ fontSize: 11, color:'#94a3b8', marginTop: 2 }}>{c.email}</div>}
        </div>
      </div>

      {c.telegram_chat_id && (
        <div style={{
          background:'#e0f2fe', padding: '6px 10px', borderRadius: 8, marginBottom: 8,
          fontSize: 11, color:'#0c4a7c', fontFamily:'monospace',
          display:'inline-flex', alignItems:'center', gap: 4,
        }}>
          <Send size={11} />{c.telegram_chat_id}
        </div>
      )}

      <div style={{
        background:'#f8fafc', padding: '8px 12px', borderRadius: 8, marginBottom: 10,
        display:'flex', justifyContent:'space-between', fontSize: 12,
      }}>
        <span style={{ color:'#64748b' }}>บิล: <b style={{ color: '#166534' }}>{c.invoice_count || 0}</b></span>
        <span style={{ color:'#64748b' }}>ยอดรวม: <b style={{ color:'#0284c7' }}>{Number(c.total_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })} ฿</b></span>
      </div>

      <div style={{ display:'flex', gap: 6 }}>
        <button onClick={() => onEdit(c)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background:'#f1f5f9', border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap: 4,
            fontSize: 13, color:'#64748b', fontWeight: 500,
          }}>
          <Edit2 size={14} />แก้ไข
        </button>
        <button onClick={() => onToggle(c)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: c.is_active ? '#fee2e2' : '#dcfce7',
            color: c.is_active ? '#dc2626' : '#16a34a',
            border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap: 4,
            fontSize: 13, fontWeight: 500,
          }}>
          <Power size={14} />{c.is_active ? 'ปิด' : 'เปิด'}
        </button>
        <button onClick={() => onDelete(c)}
          style={{
            padding: '10px 14px', borderRadius: 8,
            background:'#fff1f2', border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
          <Trash2 size={14} color="#ef4444" />
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon, isText }) {
  return (
    <div style={{
      background:'#fff', padding: 16, borderRadius: 12,
      boxShadow:'0 1px 6px rgba(0,0,0,0.05)',
    }}>
      <div style={{ display:'flex', alignItems:'center', gap: 6, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color }}>{label}</span>
      </div>
      <div style={{ fontSize: isText ? 17 : 24, fontWeight: 700, color:'#1e293b', lineHeight: 1.1 }}>
        {value}
      </div>
    </div>
  );
}

const Th = ({ children, align }) => (
  <th style={{
    padding: '12px 14px',
    textAlign: align || 'left',
    fontSize: 12, fontWeight: 600, color:'#64748b',
    textTransform: 'uppercase', letterSpacing: '0.03em',
  }}>{children}</th>
);

const Td = ({ children, align }) => (
  <td style={{
    padding: '12px 14px',
    textAlign: align || 'left',
    color:'#334155', verticalAlign: 'middle',
  }}>{children}</td>
);

const Label = ({ children, color }) => (
  <label style={{
    fontSize: 12, fontWeight: 600,
    color: color || '#64748b',
    display:'block', marginBottom: 4,
  }}>{children}</label>
);

const inputStyle = (isMobile) => ({
  width:'100%', boxSizing:'border-box',
  padding: isMobile ? '12px 14px' : '9px 12px',
  borderRadius: 8, border: '1px solid #e2e8f0',
  fontSize: isMobile ? 16 : 14, outline:'none',
  background:'#fff',
});

const IconBtn = ({ children, onClick, color, title }) => (
  <button onClick={onClick} title={title}
    style={{
      padding: 6, borderRadius: 6,
      background: 'transparent', border: '1px solid #e2e8f0',
      cursor:'pointer', color,
      display:'inline-flex', alignItems:'center', justifyContent:'center',
    }}>
    {children}
  </button>
);
